import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
  RendererStyleFlags2,
} from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Keyboard } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { NavigationService } from '../navigation/navigation.service';
import { UserService } from '../user/user.service';
import { baseUrl, openNativeAlertDialog, openNativeSettings } from '../utils/helpers';
import { ConnectivityService } from './connectivity/connectivity.service';
import { LanguageService } from '../../_shared/services/language/language/language.service';
import { SearchBarStore } from '../../_shared/components/search-bar/search-bar.store';

@Injectable()
export class CapacitorService implements OnDestroy {
  private renderer: Renderer2;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    @Inject(PLATFORM_ID) private platformId: NonNullable<unknown>,
    private navigationService: NavigationService,
    private router: Router,
    private ngZone: NgZone,
    private userService: UserService,
    private rendererFactory2: RendererFactory2,
    private connectivityService: ConnectivityService,
    private languageService: LanguageService,
    private injector: Injector,
  ) {
    this.renderer = this.rendererFactory2.createRenderer(null, null);
  }

  init() {
    if (isPlatformBrowser(this.platformId)) {
      this.connectivityService.init();

      const current = CapacitorUpdater.current();
      const latest = CapacitorUpdater.getLatest();
      console.log('current bundle ::', current);
      console.log('latest bundle ::', latest);

      CapacitorUpdater.notifyAppReady().catch(console.error);

      CapacitorUpdater.addListener('updateAvailable', console.log);
      CapacitorUpdater.addListener('updateFailed', console.log);
      CapacitorUpdater.addListener('downloadFailed', console.log);
      CapacitorUpdater.addListener('appReady', console.log);

      Keyboard.addListener('keyboardWillShow', info => {
        if (Capacitor.getPlatform() === 'ios') {
          this.renderer.setStyle(
            this.document.querySelector('.app-wrapper'),
            'padding-bottom',
            info.keyboardHeight + 'px',
            RendererStyleFlags2.Important,
          );
          if (this.document.querySelector('.generic-dialog__content')) {
            this.renderer.setStyle(
              this.document.querySelector('.generic-dialog__content'),
              'padding-bottom',
              info.keyboardHeight + 'px',
              RendererStyleFlags2.Important,
            );
          }
          if (this.document.querySelector('.create__wrapper')) {
            this.renderer.removeStyle(this.document.querySelector('.app-wrapper'), 'padding-bottom');
            this.renderer.setStyle(
              this.document.querySelector('.create__wrapper'),
              'padding-bottom',
              info.keyboardHeight + 'px',
              RendererStyleFlags2.Important,
            );
          }
          if (this.document.querySelector('.dialog-mobile__body')) {
            this.renderer.setStyle(
              this.document.querySelector('.dialog-mobile__body'),
              'padding-bottom',
              info.keyboardHeight + 'px',
              RendererStyleFlags2.Important,
            );
          }
        }
        if (this.document.querySelector('.messages__conversation--with-id')) {
          this.document.querySelector('.app-wrapper').scrollBy(0, info.keyboardHeight);
          if (Capacitor.getPlatform() === 'ios') {
            this.renderer.addClass(
              this.document.querySelector('.messages__conversation'),
              'messages__conversation--keyboard',
            );
            this.renderer.setStyle(
              this.document.querySelector('.sending-box-wrapper'),
              'bottom',
              info.keyboardHeight + 'px',
            );
          }
        }
      });

      Keyboard.addListener('keyboardDidShow', info => {
        if (Capacitor.getPlatform() === 'ios') {
          this.revealFocusedInDialog(info.keyboardHeight);
        }
      });

      Keyboard.addListener('keyboardWillHide', () => {
        if (Capacitor.getPlatform() === 'ios') {
          this.renderer.removeStyle(this.document.querySelector('.app-wrapper'), 'padding-bottom');
          if (this.document.querySelector('.generic-dialog__content')) {
            this.renderer.removeStyle(this.document.querySelector('.generic-dialog__content'), 'padding-bottom');
          }
          if (this.document.querySelector('.create__wrapper')) {
            this.renderer.removeStyle(this.document.querySelector('.create__wrapper'), 'padding-bottom');
          }
          if (this.document.querySelector('.messages__conversation--with-id')) {
            this.renderer.removeClass(
              this.document.querySelector('.messages__conversation'),
              'messages__conversation--keyboard',
            );
            this.renderer.removeStyle(this.document.querySelector('.sending-box-wrapper'), 'bottom');
          }
        }
      });

      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.window.requestAnimationFrame(() => {
          this.ngZone.run(() => {
            const slug = event.url.split(baseUrl.replace('https://', '').replace('www.', '')).pop();
            const slugLang = slug.split('/')[1];
            const decodedUrl = decodeURIComponent(slug);
            if (slug) {
              Browser.close();

              this.languageService.checkAndPrompt(slugLang);

              const langPromise = new Promise<void>(resolve => {
                if (['it', 'en', 'es', 'fr', 'de'].includes(slugLang)) {
                  this.translateService.changeLanguage(slugLang).then(() => resolve());
                } else {
                  resolve();
                }
              });

              langPromise.then(async () => {
                try {
                  await this.router.navigateByUrl('/', { skipLocationChange: true });
                  const v = await this.router.navigateByUrl(decodedUrl, {
                    skipLocationChange: false,
                    state: { refresh: true },
                  });
                  console.log(v);
                } catch (e) {
                  console.log(e);
                }
              });
            }
          });
        });
      });
      App.addListener('backButton', () => {
        const searchBar = this.injector.get(SearchBarStore);
        if (searchBar.isOpen()) {
          this.ngZone.run(() => searchBar.dismiss());
          return;
        }
        if (this.navigationService.previousUrls?.length > 1) {
          this.navigationService.back();
        } else {
          App.exitApp();
        }
      });

      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          this.userService.refreshUnreadCounts();
        }
      });

      Badge.requestPermissions().catch(error => console.error(error));

      Geolocation.checkPermissions()
        .then(permission => {
          if (permission.coarseLocation === 'denied' && permission.location === 'denied') {
            if (!!this.userService.user && !this.userService.user.refusedGeolocation) {
              openNativeAlertDialog(
                this.translateService.t('capacitor.dialog.permission-denied.geolocation.title'),
                this.translateService.t('capacitor.dialog.permission-denied.geolocation.message'),
                this.translateService.t('capacitor.dialog.permission-denied.confirm-button'),
                this.translateService.t('capacitor.dialog.permission-denied.cancel-button'),
              )
                .then(result => {
                  if (result.value) {
                    openNativeSettings();
                  }
                })
                .catch(error => console.error(error));
            }
          } else {
            Geolocation.requestPermissions({ permissions: ['coarseLocation', 'location'] })
              .then(permissionStatus => {
                if (permissionStatus.coarseLocation === 'granted' || permissionStatus.location === 'granted') {
                  this.userService.setUserRefusedGeolocation(false);
                } else {
                  this.userService.setUserRefusedGeolocation(true);
                }
              })
              .catch(error => console.error(error));
          }
        })
        .catch(error => console.error(error));
    }
  }
  private revealFocusedInDialog(keyboardHeight: number): void {
    const focused = this.document.activeElement as HTMLElement | null;
    if (!focused?.closest('.generic-dialog')) return;
    let scroller = focused.parentElement;
    while (
      scroller &&
      !(
        scroller.scrollHeight > scroller.clientHeight &&
        /auto|scroll/.test(this.window.getComputedStyle(scroller).overflowY)
      )
    ) {
      scroller = scroller.parentElement;
    }
    const overlap = focused.getBoundingClientRect().bottom - (this.window.innerHeight - keyboardHeight - 16);
    if (scroller && overlap > 0) {
      scroller.scrollBy({ top: overlap, behavior: 'smooth' });
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      Network.removeAllListeners()
        .then(() => console.log('all network listener removed'))
        .catch(error => console.error(error));
      Keyboard.removeAllListeners()
        .then(() => console.log('all keyboard listener removed'))
        .catch(error => console.error(error));
      App.removeAllListeners()
        .then(() => console.log('all app listener removed'))
        .catch(error => console.error(error));
      PushNotifications.removeAllListeners()
        .then(() => console.log('all pushNotification listener removed'))
        .catch(error => console.error(error));
    }
  }
}

export const CAPACITOR_SERVICE = new InjectionToken<CapacitorService>('capacitor');
